import { z } from 'zod';
import { Dateish, BooleanQuery, PaginationQuerySchema } from './common';

export const ClientAddressSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  cep: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
});
export type ClientAddress = z.infer<typeof ClientAddressSchema>;

export const ClientAddressBodySchema = ClientAddressSchema.omit({ id: true, clientId: true })
  .partial()
  .strict();
export type ClientAddressBody = z.infer<typeof ClientAddressBodySchema>;

export const ContactUseSchema = z.enum(['personal', 'commercial']);

export const ClientContactSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  owner: z.string().nullable(),
  email: z.string().nullable(),
  telephone: z.string().nullable(),
  use: ContactUseSchema,
});
export type ClientContact = z.infer<typeof ClientContactSchema>;

export const ClientContactBodySchema = z
  .object({
    owner: z.string().nullable(),
    email: z.string().nullable(),
    telephone: z.string().nullable(),
    use: ContactUseSchema.default('commercial'),
  })
  .partial()
  .strict();
export type ClientContactBody = z.infer<typeof ClientContactBodySchema>;

export const ClientSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  social: z.string().nullable(),
  socialRevenue: z.string().nullable(),
  status: z.boolean(),
  notes: z.string().nullable(),
  createdAt: Dateish,
  updatedAt: Dateish,
});
export type Client = z.infer<typeof ClientSchema>;

export const ClientDetailSchema = ClientSchema.extend({
  addresses: z.array(ClientAddressSchema),
  contacts: z.array(ClientContactSchema),
  openTicketsCount: z.number().int(),
  /** spec 024/026: desligado do espelho do TiFlux — os tickets dele são operados no ERP */
  operatedInErp: z.boolean(),
  /**
   * Spec 026 (FR-005): tickets do cliente com pendência de sincronização — mudou
   * no TiFlux e o ERP tem outro valor ("3 tickets mudaram no TiFlux"). Pelo
   * MESMO predicado do indicador no detalhe do ticket. 0 fora de cliente operado.
   */
  tifluxPendingTickets: z.number().int(),
});
export type ClientDetail = z.infer<typeof ClientDetailSchema>;

/**
 * GET /clients/operated-in-erp (spec 024) — os clientes operados no ERP
 * (desligados do espelho do TiFlux). É o que a tela de criação usa para
 * oferecer as mesas espelhadas: elas só recebem ticket desses clientes, e ele
 * nasce fora do espelho. Lista curta (a Minas Prime, no piloto) — só ids.
 */
export const ClientsOperatedInErpSchema = z.object({ clientIds: z.array(z.number().int()) });
export type ClientsOperatedInErp = z.infer<typeof ClientsOperatedInErpSchema>;

export const ClientListQuerySchema = PaginationQuerySchema.extend({
  /** substring match on name/social */
  search: z.string().optional(),
  /** exact name match (the Tiflux v1 gap) */
  name: z.string().optional(),
  active: BooleanQuery.optional(),
});
export type ClientListQuery = z.infer<typeof ClientListQuerySchema>;

export const ClientCreateBodySchema = z.object({
  name: z.string().min(1),
  social: z.string().nullish(),
  socialRevenue: z.string().nullish(),
  status: z.boolean().default(true),
  notes: z.string().nullish(),
});
export type ClientCreateBody = z.infer<typeof ClientCreateBodySchema>;

export const ClientPatchBodySchema = z
  .object({
    name: z.string().min(1),
    social: z.string().nullable(),
    socialRevenue: z.string().nullable(),
    status: z.boolean(),
    notes: z.string().nullable(),
  })
  .partial()
  .strict();
export type ClientPatchBody = z.infer<typeof ClientPatchBodySchema>;
